import { useState } from 'react';
import { useQuery } from 'react-query';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { DesktopLayout } from 'src/layout';
import SubscriptionsService from 'src/services/SubscriptionsService';
import { publicPage } from 'src/screens/IntentWorkspace/WorkspaceHelp';
import Copy from 'src/icons/Copy';
import Share from 'src/icons/Share';
import UserPlus from 'src/icons/UserPlus';
import BonusIcon from 'src/icons/BonusIcon';
import { formatReferralEarnings, totalReferralEarnings } from 'src/services/SubscriptionsService/referrals';
import { SUPPORT_URL } from 'src/constants/support';

const card = {
    p: { xs: 2, sm: 3 },
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px',
    bgcolor: 'var(--lm-surface)',
};
export default function Partnership() {
    const { enqueueSnackbar } = useSnackbar();
    const [promocode, setPromocode] = useState('');
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const promo = useQuery('myPromocode', () => SubscriptionsService.getMyPromocode());
    const referrals = useQuery('referrals', () => SubscriptionsService.getMyReferrals());
    const data = promo.data;
    const copy = async () => {
        if (!data?.code) return;
        try {
            await navigator.clipboard.writeText(data.code);
            enqueueSnackbar('Промокод скопирован', { variant: 'success' });
        } catch {
            enqueueSnackbar('Не удалось скопировать промокод', { variant: 'error' });
        }
    };
    const save = async () => {
        const code = promocode.trim().toUpperCase();
        if (!/^[A-Z0-9]+$/.test(code)) {
            setError('Используйте латинские буквы и цифры.');
            return;
        }
        if (code === data?.code) {
            setEditing(false);
            return;
        }
        setSaving(true);
        setError('');
        try {
            let available = false;
            try {
                await SubscriptionsService.checkPromocode(code);
            } catch (e: any) {
                const detail = e?.response?.data?.error || e?.error;
                if (detail === 'Недействительный промокод') available = true;
                else throw e;
            }
            if (!available) {
                setError('Этот промокод уже занят.');
                return;
            }
            await SubscriptionsService.updateMyPromocode({ code });
            await promo.refetch();
            setEditing(false);
            enqueueSnackbar('Промокод изменён', { variant: 'success' });
        } catch (e: any) {
            setError(e?.response?.data?.error || e?.error || 'Не удалось сохранить. Попробуйте ещё раз.');
        } finally {
            setSaving(false);
        }
    };
    const shareText = data
        ? `Лидсмания помогает находить запросы на ваши услуги. Мой промокод ${data.code}${
              data.discount_percent > 0
                  ? ` даёт скидку ${data.discount_percent}% при применении к подходящей покупке`
                  : ''
          }.`
        : '';
    const shareUrl =
        'https://t.me/share/url?url=' +
        encodeURIComponent('https://leadsmania.ru') +
        '&text=' +
        encodeURIComponent(shareText);
    return (
        <DesktopLayout hideRightSidebar>
            <Stack
                spacing={3}
                sx={{
                    py: 2,
                    px: { xs: 2, md: 0 },
                    minWidth: 0,
                    '& svg': { flexShrink: 0 },
                    '& h2': { fontSize: 22, fontWeight: 700 },
                }}
            >
                <Box>
                    <Typography
                        component="h1"
                        sx={{ fontSize: { xs: 28, sm: 36 }, fontWeight: 800 }}
                    >
                        Партнёрство
                    </Typography>
                    <Typography
                        color="text.secondary"
                        mt={1}
                    >
                        Передайте промокод клиенту перед покупкой.
                    </Typography>
                </Box>
                {promo.isLoading ? (
                    <CircularProgress size={24} />
                ) : promo.isError ? (
                    <Alert
                        severity="error"
                        action={<Button onClick={() => void promo.refetch()}>Повторить</Button>}
                    >
                        Не удалось загрузить промокод.
                    </Alert>
                ) : (
                    data && (
                        <Paper
                            elevation={0}
                            sx={card}
                        >
                            <Stack spacing={2.5}>
                                <Typography
                                    component="h2"
                                    variant="h5"
                                >
                                    Ваш промокод
                                </Typography>
                                {editing ? (
                                    <Stack spacing={2}>
                                        <TextField
                                            label="Промокод"
                                            value={promocode}
                                            onChange={(e) => setPromocode(e.target.value.toUpperCase())}
                                            disabled={saving}
                                            error={!!error}
                                            helperText={error}
                                        />
                                        <Stack
                                            direction="row"
                                            gap={1}
                                        >
                                            <Button
                                                variant="contained"
                                                onClick={() => void save()}
                                                disabled={saving}
                                            >
                                                {saving ? 'Сохраняем…' : 'Сохранить'}
                                            </Button>
                                            <Button
                                                disabled={saving}
                                                onClick={() => setEditing(false)}
                                            >
                                                Отмена
                                            </Button>
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={2}
                                            flexWrap="wrap"
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: { xs: 26, sm: 34 },
                                                    fontWeight: 700,
                                                    color: 'var(--lm-accent-ink)',
                                                    overflowWrap: 'anywhere',
                                                }}
                                            >
                                                {data.code}
                                            </Typography>
                                            <Button
                                                onClick={() => {
                                                    setPromocode(data.code);
                                                    setError('');
                                                    setEditing(true);
                                                }}
                                            >
                                                Изменить
                                            </Button>
                                        </Stack>
                                        <Stack
                                            direction="row"
                                            gap={1.5}
                                            flexWrap="wrap"
                                        >
                                            <Button
                                                variant="contained"
                                                startIcon={<Copy />}
                                                onClick={() => void copy()}
                                            >
                                                Скопировать
                                            </Button>
                                            <Button
                                                component="a"
                                                variant="outlined"
                                                startIcon={<Share />}
                                                href={shareUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Поделиться
                                            </Button>
                                        </Stack>
                                    </>
                                )}
                                <Divider />
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    gap={3}
                                >
                                    <Box flex={1}>
                                        <Typography
                                            color="text.secondary"
                                            variant="body2"
                                        >
                                            Приглашённому клиенту
                                        </Typography>
                                        <Typography
                                            fontWeight={600}
                                            mt={0.5}
                                        >
                                            {data.discount_percent}% при применении промокода
                                        </Typography>
                                    </Box>
                                    <Box flex={1}>
                                        <Typography
                                            color="text.secondary"
                                            variant="body2"
                                        >
                                            Ваше вознаграждение
                                        </Typography>
                                        <Typography
                                            fontWeight={600}
                                            mt={0.5}
                                        >
                                            {data.reward_percent}% по условиям программы
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Box>
                                    <Button
                                        component="a"
                                        href={publicPage('/products/partnership/index.html') + '#partner-faq'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ p: 0 }}
                                    >
                                        Об участии в программе ↗
                                    </Button>
                                </Box>
                            </Stack>
                        </Paper>
                    )
                )}
                {referrals.isLoading ? (
                    <CircularProgress size={24} />
                ) : referrals.isError ? (
                    <Alert
                        severity="error"
                        action={<Button onClick={() => void referrals.refetch()}>Повторить</Button>}
                    >
                        Не удалось загрузить результаты рекомендаций.
                    </Alert>
                ) : (
                    <>
                        <Paper
                            elevation={0}
                            sx={card}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={2}
                                mb={3}
                            >
                                <Typography
                                    component="h2"
                                    variant="h5"
                                >
                                    Результаты рекомендаций
                                </Typography>
                                <Button
                                    disabled={referrals.isFetching}
                                    onClick={() => void referrals.refetch()}
                                >
                                    {referrals.isFetching ? 'Обновляем…' : 'Обновить'}
                                </Button>
                            </Stack>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                gap={3}
                                divider={
                                    <Divider
                                        flexItem
                                        orientation="vertical"
                                    />
                                }
                            >
                                <Stack
                                    direction="row"
                                    gap={2}
                                    flex={1}
                                    alignItems="center"
                                >
                                    <UserPlus />
                                    <Box>
                                        <Typography
                                            color="text.secondary"
                                            variant="body2"
                                        >
                                            Клиентов в учёте
                                        </Typography>
                                        <Typography
                                            fontSize={28}
                                            fontWeight={700}
                                        >
                                            {referrals.data?.length || 0}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Stack
                                    direction="row"
                                    gap={2}
                                    flex={1}
                                    alignItems="center"
                                >
                                    <BonusIcon />
                                    <Box>
                                        <Typography
                                            color="text.secondary"
                                            variant="body2"
                                        >
                                            Расчётное вознаграждение
                                        </Typography>
                                        <Typography
                                            fontSize={28}
                                            fontWeight={700}
                                        >
                                            {formatReferralEarnings(totalReferralEarnings(referrals.data || []))}
                                            <Typography
                                                component="span"
                                                color="text.secondary"
                                                sx={{ ml: 1, fontSize: 14, fontWeight: 400 }}
                                            >
                                                баллов
                                            </Typography>
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                            <Typography
                                color="text.secondary"
                                variant="body2"
                                mt={2}
                                mb={2}
                            >
                                Суммы по клиентам за всё время. Доступный баланс баллов указан в профиле.
                            </Typography>
                            {referrals.data?.length ? (
                                <TableContainer>
                                    <Table
                                        size="small"
                                        aria-label="Вознаграждения по клиентам"
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Клиент</TableCell>
                                                <TableCell align="right">Расчёт, баллы</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {referrals.data.map((r) => (
                                                <TableRow key={r.id}>
                                                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{r.email}</TableCell>
                                                    <TableCell align="right">
                                                        {formatReferralEarnings(r.earned)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography color="text.secondary">
                                    Учтённых вознаграждений пока нет. Передайте клиенту свой промокод.
                                </Typography>
                            )}
                        </Paper>
                    </>
                )}
                <Box
                    component="details"
                    sx={{
                        '& summary': { cursor: 'pointer', fontWeight: 600 },
                        '& p': { mt: 1.5, color: 'text.secondary', fontSize: 14 },
                    }}
                >
                    <summary>Как учитываются рекомендации</summary>
                    <Typography>
                        Клиент вводит промокод перед оплатой. Применимость и итоговая скидка проверяются при оформлении
                        покупки.
                    </Typography>
                    <Typography>
                        В таблице — клиенты, по которым уже есть запись в учёте вознаграждений. Это не список всех
                        регистраций.
                    </Typography>
                    <Typography>
                        Расчётное вознаграждение может отличаться от зачисленных баллов из-за округления при каждой
                        выплате. Даты и отдельные операции в этом разделе пока не отображаются.
                    </Typography>
                </Box>
                <Box>
                    <Button
                        component="a"
                        href={SUPPORT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Вопрос по партнёрству ↗
                    </Button>
                </Box>
            </Stack>
        </DesktopLayout>
    );
}
